import type { VisualizationSpec } from "vega-embed";
import { withoutSpecPalette } from "../../../services/chartStudio/chartColorPick";
import { registerNumberFormatType } from "../../../services/chartStudio/applyNumberFormat";
import { toResponsiveSpec } from "../ChartWidget/chartResponsiveSpec";
import { withReadableFieldTitles } from "./vegaFieldTitles";
import type { VegaChartSelection, VegaChartTheme } from "./VegaChart.types";

type SpecRecord = Record<string, unknown>;

// Every Vega view this molecule (and ChartWidget, which shares this file) draws can meet a saved
// spec the server formatted with `formatType: "magicbiNumber"`, with no draft applied first.
registerNumberFormatType();

/**
 * The drawn spec fitted to the measured plot box (ChartWidget's sizing rules, root title removed:
 * the frame shows it). A layer spec has no root mark, so the sizing's `mark` key is dropped
 * rather than left undefined — Vega-Lite reads any `mark` key as a unit spec.
 */
export const toSizedSpec = (spec: VisualizationSpec, widthPx: number, heightPx: number): VisualizationSpec => {
  const sized = toResponsiveSpec(spec, widthPx, heightPx) as SpecRecord;
  if (sized.mark !== undefined) return sized as VisualizationSpec;
  const { mark: _unused, ...rest } = sized;
  return rest as VisualizationSpec;
};

/** Names of the spec's top-level value params (selections excluded) — the ones a caller may set. */
export const declaredParamNames = (spec: VisualizationSpec): string[] => {
  const params = (spec as SpecRecord).params;
  if (!Array.isArray(params)) return [];
  return params.flatMap((param) => {
    const entry = param as { name?: unknown; select?: unknown };
    return typeof entry.name === "string" && entry.select === undefined ? [entry.name] : [];
  });
};

/** The param changes to push to a view: declared names whose wanted value differs from the applied one. */
export const paramUpdates = (
  names: string[],
  wanted: Record<string, number>,
  applied: Record<string, number>,
): [string, number][] =>
  names.flatMap((name) => {
    const value = wanted[name];
    return typeof value === "number" && Number.isFinite(value) && applied[name] !== value ? [[name, value] as [string, number]] : [];
  });

/* ---------------------------------------------------------------------------------------------
 * Theme and interaction
 * ------------------------------------------------------------------------------------------- */

const asRecord = (value: unknown): SpecRecord => (typeof value === "object" && value !== null && !Array.isArray(value) ? (value as SpecRecord) : {});

/** Deep merge of plain config objects; later wins, arrays replace. */
const mergeConfig = (...configs: SpecRecord[]): SpecRecord =>
  configs.reduce<SpecRecord>((merged, config) => {
    Object.entries(config).forEach(([key, value]) => {
      const held = merged[key];
      const both = typeof held === "object" && held !== null && !Array.isArray(held) && typeof value === "object" && value !== null && !Array.isArray(value);
      merged[key] = both ? mergeConfig(held as SpecRecord, value as SpecRecord) : value;
    });
    return merged;
  }, {});

/* ---- The design's chart look (ChartTile / ChartIcon): sizes, radii and strokes, as spec defaults ---- */

const AXIS_LABEL_FONT_SIZE = 11;
const AXIS_LABEL_PADDING = 8;
const VALUE_LABEL_FONT_SIZE = 11;
const VALUE_LABEL_FONT_WEIGHT = 600;
const VALUE_LABEL_GAP = 4;
const LEGEND_LABEL_FONT_SIZE = 12;
/** Vega symbol size is an area: 64 draws an 8px square. */
const LEGEND_SYMBOL_AREA = 64;
const BAR_RADIUS_TOP = 6;
const BAR_RADIUS_BOTTOM = 2;
const BAND_PADDING_INNER = 0.25;
const BAND_PADDING_OUTER = 0.1;
const LINE_STROKE_WIDTH = 2.5;
const AREA_FILL_OPACITY = 0.14;
/** The donut ring's inner edge, as a share of the outer radius (design: r 40, stroke 16 of 50). */
const DONUT_INNER_RATIO = 0.64;
const CENTRE_TOTAL_FONT_SIZE = 16;
const CENTRE_CAPTION_FONT_SIZE = 10;
const CENTRE_CAPTION_GAP = 14;
const CENTRE_FONT_WEIGHT = 700;
/** Compact numbers ("1.42M"); the design's value labels and axis ticks. */
const COMPACT_FORMAT = ".3~s";
const COMPACT_THRESHOLD = 1000;
/** Value labels are skipped on bar charts with more bars than read at a glance (inline data only). */
const VALUE_LABEL_MAX_BARS = 12;
/** Caption under a donut's centre total. */
const CENTRE_CAPTION = "Total";
/** Marks carrying this name are chart furniture: a selection leaves them undimmed. */
export const CENTRE_LABEL_NAME = "centre-label";

const markType = (mark: unknown): string | null => (typeof mark === "string" ? mark : typeof asRecord(mark).type === "string" ? (asRecord(mark).type as string) : null);

/** The mark types a spec draws: its own, or each layer's. */
const markTypesOf = (spec: SpecRecord): string[] => {
  const own = markType(spec.mark);
  const layers = Array.isArray(spec.layer) ? spec.layer.flatMap((layer) => markTypesOf(asRecord(layer))) : [];
  return own ? [own, ...layers] : layers;
};

const firstEncoding = (spec: SpecRecord, channel: string): SpecRecord => {
  const own = asRecord(asRecord(spec.encoding)[channel]);
  if (Object.keys(own).length) return own;
  const layers = Array.isArray(spec.layer) ? spec.layer : [];
  for (const layer of layers) {
    const found = firstEncoding(asRecord(layer), channel);
    if (Object.keys(found).length) return found;
  }
  return {};
};

const COMPACT_LABEL_EXPR = `isNumber(datum.value) && abs(datum.value) >= ${COMPACT_THRESHOLD} ? format(datum.value, '${COMPACT_FORMAT}') : datum.label`;

/**
 * A plain digit-grouping number format (",", ",.2~f", ".0f", "d"): no currency, percent, SI or
 * exponent. The server's compiled specs put one on every measure axis, which prints
 * "40,000,000"; it carries no meaning a compact label would lose.
 */
const PLAIN_NUMBER_FORMAT = /^,?(\.\d+)?~?[fdr]?$/;

/** A quantitative channel whose axis may show compact labels: no label rule of its own and at most a plain number format. */
const canCompactAxis = (channel: SpecRecord): boolean => {
  if (channel.type !== "quantitative" || channel.axis === null) return false;
  const axis = asRecord(channel.axis);
  const format = axis.format;
  const plainFormat = format === undefined || (typeof format === "string" && PLAIN_NUMBER_FORMAT.test(format));
  return plainFormat && axis.labelExpr === undefined && axis.formatType === undefined;
};

/** A quantitative x axis (horizontal bars) compacts big numbers like the y axis does. */
const compactXDefaults = (spec: SpecRecord): SpecRecord =>
  canCompactAxis(firstEncoding(spec, "x")) ? { axisX: { labelExpr: COMPACT_LABEL_EXPR } } : {};

const DISCRETE = ["nominal", "ordinal"];

/** A chart laid along x (horizontal bar, lollipop, dot, dumbbell, gantt…): a discrete category down y, a value or time across x. */
const isHorizontal = (spec: SpecRecord): boolean => {
  const xType = firstEncoding(spec, "x").type;
  return DISCRETE.includes(firstEncoding(spec, "y").type as string) && typeof xType === "string" && !DISCRETE.includes(xType);
};

/** The design rounds a bar's far end: its top when upright, its right end when horizontal. */
const barCorners = (horizontal: boolean): SpecRecord =>
  horizontal
    ? { cornerRadiusTopRight: BAR_RADIUS_TOP, cornerRadiusBottomRight: BAR_RADIUS_TOP, cornerRadiusTopLeft: BAR_RADIUS_BOTTOM, cornerRadiusBottomLeft: BAR_RADIUS_BOTTOM }
    : { cornerRadiusTopLeft: BAR_RADIUS_TOP, cornerRadiusTopRight: BAR_RADIUS_TOP, cornerRadiusBottomLeft: BAR_RADIUS_BOTTOM, cornerRadiusBottomRight: BAR_RADIUS_BOTTOM };

/** Chart furniture defaults. Anything a spec's own config sets wins over these. */
/** Plot widths under this rotate the x labels 45 degrees. */
const X_LABEL_ROTATE_BELOW_PX = 720;

const designDefaults = (spec: SpecRecord): SpecRecord => {
  const marks = markTypesOf(spec);
  const isRound = marks.includes("arc");
  const hasLine = marks.some((mark) => mark === "line" || mark === "area");
  const compactY = canCompactAxis(firstEncoding(spec, "y"));
  const horizontal = isHorizontal(spec);
  return {
    axis: { title: null, ticks: false, labelFontSize: AXIS_LABEL_FONT_SIZE, labelFontWeight: "normal", labelPadding: AXIS_LABEL_PADDING, grid: false },
    // Crowded x labels rotate first (narrow plots), and none are dropped; wide plots thin by a parity step only where horizontal labels would collide.
    axisX: { domain: true, labelAngle: { expr: `width < ${X_LABEL_ROTATE_BELOW_PX} ? -45 : 0` }, labelOverlap: { expr: `width < ${X_LABEL_ROTATE_BELOW_PX} ? false : "parity"` } },
    // Category names down the side thin out like the x axis's rather than print over each other.
    axisY: { domain: false, grid: hasLine, ...(compactY ? { labelExpr: COMPACT_LABEL_EXPR } : {}), ...(horizontal ? { labelOverlap: "parity" } : {}) },
    scale: { bandPaddingInner: BAND_PADDING_INNER, bandPaddingOuter: BAND_PADDING_OUTER },
    bar: barCorners(horizontal),
    line: { strokeWidth: LINE_STROKE_WIDTH, strokeJoin: "round", strokeCap: "round" },
    area: { fillOpacity: AREA_FILL_OPACITY, line: { strokeWidth: LINE_STROKE_WIDTH } },
    legend: {
      title: null,
      orient: isRound ? "right" : "top",
      symbolType: "square",
      symbolSize: LEGEND_SYMBOL_AREA,
      labelFontSize: LEGEND_LABEL_FONT_SIZE,
    },
  };
};

const NO_VALUE_LABEL_CHANNELS = ["color", "xOffset", "yOffset", "column", "row", "facet", "detail", "theta", "radius"];

const inlineRowCount = (spec: SpecRecord): number | null => {
  const values = asRecord(spec.data).values;
  return Array.isArray(values) ? values.length : null;
};

/** Format for a total or value, copied from the field's own when it has one. */
const valueTextEncoding = (source: SpecRecord): SpecRecord => ({
  ...(source.field !== undefined ? { field: source.field } : {}),
  ...(source.aggregate !== undefined ? { aggregate: source.aggregate } : {}),
  type: "quantitative",
  format: asRecord(source.axis).format ?? COMPACT_FORMAT,
});

/**
 * A plain vertical single-series bar chart with its value above each bar (and no value axis, as
 * in the design). Anything layered, faceted, stacked by colour, set by the spec itself, or with
 * more than VALUE_LABEL_MAX_BARS bars is returned as drawn.
 */
export const withBarValueLabels = (spec: SpecRecord, color: string): SpecRecord => {
  if ("layer" in spec || "facet" in spec || "repeat" in spec || "hconcat" in spec || "vconcat" in spec || "concat" in spec) return spec;
  if (markType(spec.mark) !== "bar") return spec;
  const encoding = asRecord(spec.encoding);
  const x = asRecord(encoding.x);
  const y = asRecord(encoding.y);
  const rows = inlineRowCount(spec);
  const eligible =
    DISCRETE.includes(x.type as string) &&
    y.type === "quantitative" &&
    y.stack === undefined &&
    y.axis === undefined &&
    NO_VALUE_LABEL_CHANNELS.every((channel) => encoding[channel] === undefined) &&
    (rows === null || rows <= VALUE_LABEL_MAX_BARS);
  if (!eligible) return spec;
  const { mark, encoding: _own, ...rest } = spec;
  const bars = { ...encoding, y: { ...y, axis: null } };
  return {
    ...rest,
    layer: [
      { mark, encoding: bars },
      {
        mark: { type: "text", baseline: "bottom", dy: -VALUE_LABEL_GAP, fontSize: VALUE_LABEL_FONT_SIZE, fontWeight: VALUE_LABEL_FONT_WEIGHT, color },
        encoding: { x: encoding.x, y: bars.y, text: valueTextEncoding(y) },
      },
    ],
  };
};

/**
 * The design's structure: a plain vertical single-series bar chart gets its value above each bar
 * (and no value axis, as in the design); a plain pie gets a donut ring with the total in the
 * centre. Specs that are layered, faceted, stacked by colour, or set a thing themselves are left
 * as drawn.
 */
const withDesignMarks = (spec: SpecRecord, theme: VegaChartTheme): SpecRecord => {
  if ("layer" in spec || "facet" in spec || "repeat" in spec || "hconcat" in spec || "vconcat" in spec || "concat" in spec) return spec;
  const type = markType(spec.mark);
  const encoding = asRecord(spec.encoding);
  if (type === "bar") return withBarValueLabels(spec, theme.muted);
  if (type === "arc") {
    const theta = asRecord(encoding.theta);
    if (theta.type !== "quantitative" || encoding.radius !== undefined) return spec;
    const arc = asRecord(typeof spec.mark === "string" ? {} : spec.mark);
    const mark = { ...arc, type: "arc", innerRadius: arc.innerRadius ?? { expr: `${DONUT_INNER_RATIO} * min(width, height) / 2` } };
    const centred = { align: "center", baseline: "middle", x: { expr: "width / 2" }, name: CENTRE_LABEL_NAME };
    const { mark: _mark, ...rest } = spec;
    return {
      ...rest,
      layer: [
        { mark, encoding },
        {
          mark: { type: "text", ...centred, y: { expr: "height / 2" }, fontSize: CENTRE_TOTAL_FONT_SIZE, fontWeight: CENTRE_FONT_WEIGHT, color: theme.text },
          encoding: { text: valueTextEncoding({ ...theta, aggregate: theta.aggregate ?? "sum" }) },
        },
        {
          mark: { type: "text", ...centred, y: { expr: `height / 2 + ${CENTRE_CAPTION_GAP}` }, fontSize: CENTRE_CAPTION_FONT_SIZE, color: theme.faint },
          encoding: { text: { value: CENTRE_CAPTION } },
        },
      ],
    };
  }
  return spec;
};

/**
 * The page theme applied to a drawn spec, in the design's chart look (see `designDefaults` and
 * `withDesignMarks`). Chart chrome colours (text, axes, grid, legend, background, font) always
 * follow the theme so the chart reads in light and dark; the theme's series palette beats a
 * palette the spec carries (see `withoutSpecPalette`) unless the user picked colours; the design's
 * sizes, radii and strokes are only defaults a spec's own config can override.
 */
export const withChartTheme = (themed: VisualizationSpec, theme: VegaChartTheme): VisualizationSpec => {
  // The theme's palette wins over one the spec carries, unless the user picked colours in Chart Studio.
  const spec = withoutSpecPalette(themed);
  const record = withReadableFieldTitles(withDesignMarks(spec as SpecRecord, theme) as VisualizationSpec) as SpecRecord;
  // Vega-Lite only calls a `formatType` such as the server's "magicbiNumber" when customFormatTypes is on.
  const defaults: SpecRecord = mergeConfig({ customFormatTypes: true, range: { category: theme.palette }, mark: { color: theme.accent } }, designDefaults(record));
  const chrome: SpecRecord = {
    background: "transparent",
    font: theme.font,
    view: { stroke: null },
    title: { color: theme.text },
    axis: { labelColor: theme.faint, titleColor: theme.muted, gridColor: theme.border, domainColor: theme.border, tickColor: theme.border },
    legend: { labelColor: theme.muted, titleColor: theme.muted },
    header: { labelColor: theme.muted, titleColor: theme.muted },
  };
  return { ...(withReadableFieldTitles(record as VisualizationSpec) as SpecRecord), config: mergeConfig(defaults, asRecord(record.config), chrome) } as VisualizationSpec;
};

/**
 * The theme's look as config only, for charts drawn as they were built (chat-built charts, story
 * tiles): palette, mark colour, chrome colours, font, axis, grid and legend furniture. Never
 * touches data, values, marks or encodings (no value-label layer, no donut, no label expressions),
 * and a spec's own config still wins over the defaults.
 */
export const withThemeConfig = (themed: VisualizationSpec, theme: VegaChartTheme): VisualizationSpec => {
  // The theme's palette wins over one the spec carries, unless the user picked colours in Chart Studio.
  const record = withoutSpecPalette(themed) as SpecRecord;
  // Vega-Lite only calls a `formatType` such as the server's "magicbiNumber" when customFormatTypes is on.
  const defaults: SpecRecord = mergeConfig({ customFormatTypes: true, range: { category: theme.palette }, mark: { color: theme.accent } }, designDefaults(record), compactXDefaults(record));
  const chrome: SpecRecord = {
    background: "transparent",
    font: theme.font,
    view: { stroke: null },
    title: { color: theme.text },
    axis: { labelColor: theme.faint, titleColor: theme.muted, gridColor: theme.border, domainColor: theme.border, tickColor: theme.border },
    legend: { labelColor: theme.muted, titleColor: theme.muted },
    header: { labelColor: theme.muted, titleColor: theme.muted },
  };
  return { ...withReadableFieldTitles(record as VisualizationSpec), config: mergeConfig(defaults, asRecord(record.config), chrome) } as VisualizationSpec;
};

/** Opacity of marks that aren't the selected member(s) while a selection is shown. */
export const UNSELECTED_OPACITY = 0.3;

/** A Vega expression true for a datum whose `field` is one of `values` (JSON-quoted, never raw). */
export const selectionTest = (selection: VegaChartSelection): string =>
  `indexof(${JSON.stringify(selection.values)}, datum[${JSON.stringify(selection.field)}]) >= 0`;

const withOpacity = (unit: SpecRecord, test: string): SpecRecord =>
  asRecord(unit.mark).name === CENTRE_LABEL_NAME
    ? unit
    : { ...unit, encoding: { ...asRecord(unit.encoding), opacity: { condition: { test, value: 1 }, value: UNSELECTED_OPACITY } } };

/**
 * A clickable chart shows a pointer over its marks; a selection dims every mark but the selected
 * members (unit specs and each layer that draws a mark). No selection or no values = unchanged marks.
 */
export const withSelection = (
  spec: VisualizationSpec,
  selection: VegaChartSelection | null | undefined,
  clickable: boolean,
): VisualizationSpec => {
  let record = spec as SpecRecord;
  if (clickable) record = { ...record, config: mergeConfig(asRecord(record.config), { mark: { cursor: "pointer" } }) };
  if (!selection || selection.values.length === 0) return record as VisualizationSpec;
  const test = selectionTest(selection);
  if ("mark" in record) return withOpacity(record, test) as VisualizationSpec;
  if (Array.isArray(record.layer)) {
    return { ...record, layer: record.layer.map((layer) => ("mark" in asRecord(layer) ? withOpacity(asRecord(layer), test) : layer)) } as VisualizationSpec;
  }
  return record as VisualizationSpec;
};

/** The data row behind a clicked Vega scenegraph item; null for a click on no mark (or an axis). */
export const clickedDatum = (item: unknown): Record<string, unknown> | null => {
  const datum = asRecord(item).datum;
  return typeof datum === "object" && datum !== null && !Array.isArray(datum) ? (datum as Record<string, unknown>) : null;
};
